// Stores
import { useSearchResultsStore } from '@white-label-store/search-results';

// Types
import type {
  LoungeFormData,
  ParkingFormData,
  TravelInsuranceFormData
} from '@white-label-types/component';
import type {
  productTypeId,
  SearchProducts,
} from '@white-label-types/parking-booking';

// Packages
import { datadogRum } from "@datadog/browser-rum";
import {
  getProductSearchResults,
  searchProductAvailability,
} from '@white-label-helper/api-parkings-product';
import { createSearchCriteria } from '@white-label-helper/data-parsers-create-search-criteria';
import { buildSearchResults, buildSearchResultsForTerminalFlights } from '@white-label-helper/data-parsers-search-results';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import {
  ACTION_METRIC,
  addCustomMetric,
  CONTEXT_KEYS,
  ERROR_METRIC,
  METRIC_TYPE
} from '@white-label-helper/shared-helpers';
import { TerminalFlight } from '@white-label-types/terminal-flight';
import { v4 as uuidv4 } from 'uuid';

type AdditionalFormData = {
  parking?: ParkingFormData;
  lounges?: LoungeFormData;
  fasttrack?: LoungeFormData;
}

export async function fetchProductSearchResults(
  searchId: string,
  airportCode: string | undefined,
  productId: productTypeId
) {
  const store = useSearchResultsStore();
  const startTime = performance.now();
  const is_sync = getAppVariable('is_sync');
  return new Promise((resolve, reject) => {
    getProductSearchResults(searchId).then((response) => {
      addCustomMetric(METRIC_TYPE.TRACKING, ACTION_METRIC.SEARCH_COMPLETED, {
        is_simple_search: String(is_sync),
        is_classic_search: String(!is_sync),
        event: 'SearchCompletedEvent',
        airport_code: airportCode,
      }, startTime, CONTEXT_KEYS.SEARCH_SERVICE, airportCode);

      store.updatePlaybooks(response.body?.data?.playbooks[productId]);
      if (response.body.data.search.inventory_metadata) {
        store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
      }

      if (response.body.data.errors) {
        store.updateSearchResultsErrors(response.body.data.errors);
      }

      const results = response.body.data?.results;
      const productData = results?.[productId];
      const inventoryProvider = productData?.[0]?.inventory_provider;

      if (response.body.data.booking_fee) {
        store.updateBookingFee(response.body.data.booking_fee)
      }

      if (productId == 'fasttrack' && inventoryProvider === "copenhagen-optimization" && Array.isArray(productData)) {
        resolve({ results: buildSearchResultsForTerminalFlights(productData), is_more_coming: response.body.data.is_more_coming });
      } else {
        // If multiple product type results exist, store them in search results store
        const multiProductTypeKeys = Object.keys(results);
        if (multiProductTypeKeys.length) {
          multiProductTypeKeys.forEach((key) => {
            store.updateSearchResultsByProductType({
              searchResults: buildSearchResults(response.body.data.results[key], key as productTypeId),
              productType: key as productTypeId,
            });
          });
        }
        resolve({ results: buildSearchResults(productData, productId), is_more_coming: response.body.data.is_more_coming });
      }
    }).catch((error) => {
      console.error("Error fetching product search results:", error);
      reject(error);
    })
  });
}

export function searchPollingHelper() {
  const startTime = performance.now();
  const is_sync = getAppVariable('is_sync');

  function pollProductSearchResults(
    searchId: string,
    airportCode: string | undefined,
    productId: productTypeId,
    initialInterval: number = 600
  ) {
    return new Promise((resolve, reject) => {
      let interval = initialInterval;
      let attempts = 0;
      function poll() {
        attempts++;
        if (attempts > 10) {
          reject(new Error('Polling timed out after 10 attempts'));
          return;
        }
        fetchProductSearchResults(searchId, airportCode, productId)
          .then((resp) => {
            if (resp.is_more_coming === false) {
              resolve(resp.results);
            } else {
              interval = attempts > 1 ? Math.min(Math.floor(interval * 1.5), 8000) : initialInterval;
              setTimeout(poll, interval);
            }
          })
          .catch((error) => {
            if (!error.product_code || error.product_code === getAppVariable('primary_product_type')) {
              reject(new Error(ERROR_METRIC.SEARCH_PRODUCT_FAILED_EVENT));
            } else {
              addCustomMetric(METRIC_TYPE.ERROR, ERROR_METRIC.SECONDARY_PRODUCT_ERROR, {
                is_simple_search: String(is_sync),
                is_classic_search: String(!is_sync),
                failure_response: error.error?.response || "An error occurred",
              }, startTime, CONTEXT_KEYS.SEARCH_SERVICE, airportCode);
              interval = Math.min(Math.floor(interval * 1.5), 8000);

              setTimeout(poll, interval);
            }
          });
      }
      poll();
    });
  }

  function searchProducts(
    formData: ParkingFormData | LoungeFormData | TravelInsuranceFormData,
    productId: productTypeId = 'parking',
    locale: string = '',
    additionalFormData?: AdditionalFormData,
  ) {
    const startTime = performance.now();
    const is_sync = getAppVariable('is_sync');
    const airportCode = formData.airport as string | undefined;

    let searchId: string;
    return new Promise<SearchProducts[] | TerminalFlight[]>((resolve, reject) => {

      const store = useSearchResultsStore();
      searchId = uuidv4();
      store.updateDateTimeOfLastSearch(new Date());

      let language = getAppVariable('default_language');
      if (locale) {
        language = locale;
      } else {
        language = this.$i18n.locale;
      }

      const searchCriteria = createSearchCriteria(formData, searchId, productId, language);

      // If `additionalFormData` is defined, create search criteria for each additional product type
      if (additionalFormData !== undefined) {
        (Object.keys(additionalFormData) as (keyof AdditionalFormData)[]).forEach((key) => {
          if (additionalFormData[key] === undefined) return;
          searchCriteria.criteria[key] = createSearchCriteria(
            additionalFormData[key],
            searchId,
            key,
            language
          )['criteria'][key];
        });
      }


      store.updateSearchToken(searchId);

      return searchProductAvailability(searchCriteria)
        .then((response) => {
          addCustomMetric(METRIC_TYPE.TRACKING, ACTION_METRIC.SEARCH_PRODUCT_AVAILABILITY_SUCCESS, {
            searchCriteria: JSON.stringify(searchCriteria.criteria),
            searchId: searchCriteria.token,
          }, startTime, CONTEXT_KEYS.SEARCH_SERVICE, airportCode);

          store.updatePlaybooks(response.body.data.playbooks[productId]);
          if (response.body.data.search.inventory_metadata) {
            store.updateInventoryMetadata(response.body.data.search.inventory_metadata);
          }

          if (response.body.data.errors) {
            store.updateSearchResultsErrors(response.body.data.errors);
          }

          if (response.body.data.booking_fee) {
            store.updateBookingFee(response.body.data.booking_fee)
          }

          if (!response.body.data.is_more_coming) {
            // If multiple product type results exist, store them in search results store
            const multiProductTypeKeys = Object.keys(response.body.data?.results);
            if (multiProductTypeKeys.length) {
              multiProductTypeKeys.forEach((key) => {
                store.updateSearchResultsByProductType({
                  searchResults: buildSearchResults(
                    response.body.data.results[key],
                    key as productTypeId
                  ),
                  productType: key as productTypeId,
                })
              });
            }
            resolve(
              buildSearchResults(
                response.body.data.results[productId],
                productId
              )
            );
          } else {
            pollProductSearchResults(searchId, airportCode, productId)
              .then(resolve)
              .catch(reject);
          }
        })
        .catch((error: { message: string }) => {
          datadogRum.addError(
            new Error('searchProductAvailability failed', { cause: error })
          );

          let data = null;
          try {
            data = JSON.parse(error.message);
          } catch (e) {
            console.log('possible error: ', e);
            datadogRum.addError('searchProductAvailability failed', {
              'search_service': {
                ...data,
              }
            });
          }
          reject(error);
        });
    })
      .catch((e) => {
        addCustomMetric(METRIC_TYPE.ERROR, ERROR_METRIC.SEARCH_EXCEPTION, {
          is_simple_search: String(is_sync),
          is_classic_search: String(!is_sync),
          failure_response: e.message,
          search_id: searchId,
        }, startTime, CONTEXT_KEYS.SEARCH_SERVICE, airportCode);

        throw e;
      });
  }

  return {
    searchProducts
  };
}
